import React from 'react';

import PropTypes from 'prop-types';
import {
  FaCircleQuestion,
  FaArrowRightFromBracket,
  FaRightToBracket,
} from 'react-icons/fa6';
import styled, { css } from 'styled-components';

import useHelpPageUrl from '~/layout/utils/useHelpPageUrl';
import { Link } from '~/techstyle-shared/next-routes';
import { TertiaryNavItem } from '~/techstyle-shared/react-builder-models';
import { desktop } from '~/techstyle-shared/react-components';

import { getBuilderDataAnalyticsProps } from '../utils/getBuilderDataAnalyticsProps';

import Stores from './Stores';

const iconMap = {
  [TertiaryNavItem.ICONS.QUESTION]: <FaCircleQuestion size="14" />,
  [TertiaryNavItem.ICONS.LOGOUT]: <FaArrowRightFromBracket size="14" />,
  [TertiaryNavItem.ICONS.LOGIN]: <FaRightToBracket size="14" />,
};

const Wrapper = styled.a`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  line-height: 1.1;
  gap: 6px;
  font-size: 14px;

  ${desktop`
    font-size: 12px;
    padding: 0px 24px;
  `}

  ${({ theme }) => theme.builderHeader.tertiaryNavItem.wrapper}
`;

const StyledLabel = styled.div`
  font-weight: 600;

  ${({ $hasUrl }) =>
    $hasUrl &&
    css`
      &:hover {
        font-weight: 700;
        text-decoration: underline;
      }
    `};

  ${({ theme }) => theme.builderHeader.tertiaryNavItem.styledLabel}
`;

function CustomNavItem({
  isFaqLink,
  linkUrl,
  linkText,
  dataAnalytics,
  linkIcon,
}) {
  const helpPageUrl = useHelpPageUrl();
  const hasUrl = !!linkUrl;

  if (hasUrl && isFaqLink) {
    linkUrl = helpPageUrl;
  }

  const renderTag = () => (
    <Wrapper
      aria-label={linkText}
      data-auto-tag={`tertiary-nav-${linkText.replace(/ /g, '-')}`}
      {...getBuilderDataAnalyticsProps(dataAnalytics)}
    >
      {iconMap[linkIcon]}
      <StyledLabel $hasUrl={hasUrl}>{linkText}</StyledLabel>
    </Wrapper>
  );

  return hasUrl ? (
    <Link href={linkUrl} passHref>
      {renderTag()}
    </Link>
  ) : (
    renderTag()
  );
}

CustomNavItem.propTypes = {
  dataAnalytics: PropTypes.shape({
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  isFaqLink: PropTypes.bool,
  linkIcon: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
};

function StoresNavItem({ dataAnalytics, linkUrl }) {
  const hasUrl = !!linkUrl;

  const renderTag = () => (
    <Wrapper
      aria-label="Stores"
      data-auto-tag="tertiary-nav-stores"
      {...getBuilderDataAnalyticsProps(dataAnalytics)}
    >
      <Stores hasUrl={hasUrl} />
    </Wrapper>
  );

  return hasUrl ? (
    <Link href={linkUrl} passHref>
      {renderTag()}
    </Link>
  ) : (
    renderTag()
  );
}

StoresNavItem.propTypes = {
  dataAnalytics: PropTypes.shape({
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  linkUrl: PropTypes.string,
};

function BuilderTertiaryNavItem({ type, ...rest }) {
  switch (type) {
    case TertiaryNavItem.TYPES.STORES:
      return <StoresNavItem {...rest} />;
    case TertiaryNavItem.TYPES.CUSTOM:
    default:
      return <CustomNavItem {...rest} />;
  }
}

BuilderTertiaryNavItem.propTypes = {
  dataAnalytics: PropTypes.shape({
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  isFaqLink: PropTypes.string,
  linkIcon: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  type: PropTypes.string,
};

export default BuilderTertiaryNavItem;
